import { useSyncExternalStore } from "react";

/**
 * The file archive on the studio PC (lm-infra `docs/file-archive.md`) has two
 * addresses: the public one (read and share) and the VPN one (also uploads).
 * The browser cannot tell whether the VPN is reachable, so people choose on
 * /file-archive and may have the sidebar open their choice directly.
 */
export const FILE_ARCHIVE_URLS = {
  public: "https://file-archive.livemotion.ch",
  vpn: "http://file-archive-vpn.livemotion.ch",
} as const;

export type FileArchiveMode = keyof typeof FILE_ARCHIVE_URLS;

const KEY = "file-archive-mode";
const EVENT = "file-archive-mode";

export function getFileArchiveMode(): FileArchiveMode | null {
  try {
    const v = localStorage.getItem(KEY);
    return v === "public" || v === "vpn" ? v : null;
  } catch {
    return null;
  }
}

export function setFileArchiveMode(mode: FileArchiveMode | null) {
  if (mode) localStorage.setItem(KEY, mode);
  else localStorage.removeItem(KEY);
  window.dispatchEvent(new Event(EVENT));
}

/** The remembered address, if any: the sidebar entry opens it directly. */
export function fileArchiveDirectHref(): string | undefined {
  const mode = getFileArchiveMode();
  return mode ? FILE_ARCHIVE_URLS[mode] : undefined;
}

function subscribe(onChange: () => void) {
  window.addEventListener(EVENT, onChange);
  window.addEventListener("storage", onChange);
  return () => {
    window.removeEventListener(EVENT, onChange);
    window.removeEventListener("storage", onChange);
  };
}

export function useFileArchiveMode(): FileArchiveMode | null {
  return useSyncExternalStore(subscribe, getFileArchiveMode, () => null);
}
